//react router dom import
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import { logEvent } from 'firebase/analytics';
import { analytics } from './firebase';
import { trackToolUse, isTrackingEnabled } from './utils/metrics';
import { useRouteSeo } from './utils/seo';

//TO DEPLOY:
//npm run build
// node_modules/.bin/firebase deploy

//TO RUN LOCALLY:
//npm start

//page imports
import Landing from './pages/Landing';
import Home from './pages/Home';
import Guides from './pages/Guides';
import Guide from './pages/Guide';
import CodeValidation from './pages/CodeValidation';
import Privacy from './pages/Privacy';
import TestPage from "./pages/TestPage";
import DVRTime from "./pages/DVRTime";
import UTCConverter from "./pages/UTCConverter";
import CommonAnalysis from "./pages/CommonAnalysis";
import PreservationTool from './pages/Preservation';
import WestchesterLPRTracker from './pages/WestchesterLPRTracker';
import LPRPatternAnalyzer from './pages/LPRPatternAnalyzer';
import FBScan from './pages/FBScan';
import AppleLegalProcessor from './pages/AppleLegalProcessor';
import SnapchatProcessor from './pages/SnapchatProcessor';
import CDRAnalyzer from './pages/CDRAnalyzer';
import ExifViewer from './pages/ExifViewer';
import InstagramScan from './pages/InstagramScan';
import SnapchatWarrantProcessor from './pages/SnapchatWarrantProcessor';
import AppleLegalDownload from './pages/AppleLegalDownload';
import Apple2703dParser from './pages/Apple2703dParser';
import SubpoenaRider from './pages/SubpoenaRider';
import VideoCanvass from './pages/VideoCanvass';
import AreaSearch from './pages/AreaSearch';
import CaseTimeline from './pages/CaseTimeline';
import IPLookup from './pages/IPLookup';
import PhoneLookup from './pages/PhoneLookup';
import MapSearch from './pages/MapSearch';
import PingMapper from './pages/PingMapper';
import Transcribe from './pages/Transcribe';
import AudioConverter from './pages/AudioConverter';
import NotFound from './pages/NotFound';
import HashVerifier from './pages/HashVerifier';
import VinDecoder from './pages/VinDecoder';
import ImageConverter from './pages/ImageConverter';
import Admin from './pages/Admin';
import TagTail from './pages/TagTail'; // TagTail promo — see TagTail.js to remove
import AnnouncementBanner from './components/AnnouncementBanner';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

// Friendly tool names for Google Analytics, keyed by route.
const TOOL_NAMES = {
  '/': 'Landing',
  '/tools': 'Tools Dashboard',
  '/guides': 'Guides',
  '/code-validation': 'Code Validation',
  '/privacy': 'Privacy Policy',
  '/dvrtime': 'DVR Time Converter',
  '/utcconverter': 'UTC Converter',
  '/commonanalysis': 'Common Analysis',
  '/preservation': 'Preservation Tool',
  '/lprtracker': 'REKOR / ALPR Tracker',
  '/lprpattern': 'LPR Pattern Analyzer',
  '/fbscan': 'Facebook Warrant Processor',
  '/applelegal': 'Apple Legal Process Organizer',
  '/snapchat': 'Snapchat Subpoena Processor',
  '/cdranalyzer': 'Phone Record Processing',
  '/exif': 'EXIF Viewer',
  '/instagramscan': 'Instagram Warrant Processor',
  '/snapchatwarrant': 'Snapchat Warrant Processor',
  '/applelegaldownload': 'Apple Legal Process Download',
  '/apple2703d': 'Apple Subpoena & 2703(d) Parser',
  '/subpoenarider': 'Subpoena Rider Generator',
  '/canvass': 'Video Canvass Tracker',
  '/areasearch': 'Area Search Coordinator',
  '/timeline': 'Case Timeline Builder',
  '/iplookup': 'IP and Domain Intelligence',
  '/phonelookup': 'Commercial Phone Number Lookup',
  '/mapsearch': 'Map Search',
  '/pingmapper': 'Ping Mapper',
  '/testPage': 'Test Page',
};

// Fires a Google Analytics event on every route change so each tool's usage is
// tracked centrally (one place, automatic for current and future tools).
function AnalyticsTracker() {
  const { pathname } = useLocation();
  useEffect(() => {
    // Never send a secret capability id anywhere — collapse /canvass/<id> and
    // /areasearch/<id> to their stable, id-free path.
    const path = pathname.startsWith('/canvass/') ? '/canvass'
      : pathname.startsWith('/areasearch/') ? '/areasearch'
      : pathname.startsWith('/timeline/') ? '/timeline'
      : pathname;
    const tool = TOOL_NAMES[path] || path;

    // Self-owned Firestore counters (survive ad/DNS blockers). Guarded internally
    // so it never fires on localhost / during local development.
    trackToolUse(path, tool);

    // Google Analytics (for the users who don't block it) — deployed site only.
    if (!analytics || !isTrackingEnabled()) return;
    logEvent(analytics, 'page_view', {
      page_title: tool,
      page_path: path,
      page_location: window.location.origin + path,
    });
    logEvent(analytics, 'tool_view', { tool, path });
  }, [pathname]);
  return null;
}

// Sets each route's title, description and canonical URL from data/seoPages.js.
function RouteSeo() {
  const { pathname } = useLocation();
  useRouteSeo(pathname);
  return null;
}

function App() {

	return (
		<CssBaseline>
			<Router>
			<ScrollToTop />
			<AnalyticsTracker />
			<RouteSeo />
			<AnnouncementBanner />
			<Routes>
				<Route path="/" element={<Landing/>}/>
				<Route path="/tools" element={<Home/>}/>
				<Route path="/guides" element={<Guides/>}/>
				<Route path="/guides/:slug" element={<Guide/>}/>
				<Route path="/code-validation" element={<CodeValidation/>}/>
				<Route path="/privacy" element={<Privacy/>}/>
				<Route path="/testPage" element={<TestPage/>}/>
				<Route path="/dvrtime" element={<DVRTime/>}/>
				<Route path="/utcconverter" element={<UTCConverter/>}/>
				<Route path="/commonanalysis" element={<CommonAnalysis/>}/>
				<Route path="/preservation" element={<PreservationTool/>}/>
				<Route path="/lprtracker" element={<WestchesterLPRTracker/>}/>
				<Route path="/lprpattern" element={<LPRPatternAnalyzer/>}/>
				<Route path="/fbscan" element={<FBScan/>}/>
			<Route path="/applelegal" element={<AppleLegalProcessor/>}/>
			<Route path="/snapchat" element={<SnapchatProcessor/>}/>
			<Route path="/cdranalyzer" element={<CDRAnalyzer/>}/>
			<Route path="/exif" element={<ExifViewer/>}/>
			<Route path="/instagramscan" element={<InstagramScan/>}/>
			<Route path="/snapchatwarrant" element={<SnapchatWarrantProcessor/>}/>
			<Route path="/applelegaldownload" element={<AppleLegalDownload/>}/>
			<Route path="/apple2703d" element={<Apple2703dParser/>}/>
			<Route path="/subpoenarider" element={<SubpoenaRider/>}/>
			<Route path="/providers" element={<SubpoenaRider/>}/>
			<Route path="/canvass" element={<VideoCanvass/>}/>
			<Route path="/canvass/:canvassId" element={<VideoCanvass/>}/>
			<Route path="/areasearch" element={<AreaSearch/>}/>
			<Route path="/areasearch/:searchId" element={<AreaSearch/>}/>
			<Route path="/timeline" element={<CaseTimeline/>}/>
			<Route path="/timeline/:timelineId" element={<CaseTimeline/>}/>
			<Route path="/iplookup" element={<IPLookup/>}/>
			<Route path="/phonelookup" element={<PhoneLookup/>}/>
			<Route path="/mapsearch" element={<MapSearch/>}/>
			<Route path="/pingmapper" element={<PingMapper/>}/>
			<Route path="/transcribe" element={<Transcribe/>}/>
			<Route path="/audioconverter" element={<AudioConverter/>}/>
			<Route path="/admin" element={<Admin/>}/>
			<Route path="/hashverifier" element={<HashVerifier/>}/>
			<Route path="/vindecoder" element={<VinDecoder/>}/>
			<Route path="/imageconverter" element={<ImageConverter/>}/>
			<Route path="/tagtail" element={<TagTail/>}/>{/* TagTail promo */}
			<Route path="*" element={<NotFound/>}/>
</Routes>
		</Router>

		</CssBaseline>

	);
}

export default App;
